import { Evaluator, EvaluationCase, MergedEvaluationCase } from "./types"
import { useTranslation } from "react-i18next"
import { GeneratingSpinner } from "./StatusIndicator"
import AttachmentBadge from "@/components/Conversation/AttachmentBadge"
import { X } from "lucide-react"
import {
  useStopEvaluationApi,
  makeStopEvaluationRequest,
} from "@/hooks/api/eval"
import useToast from "@/hooks/useToast"
import AverageScoreCard from "./AverageScoreCard"
import { format } from "date-fns"

import {
  summarizeAttachments,
  safe,
  summarizeEvaluationTable,
} from "@/components/Copilot/summarizers"
import { EvaluationResult } from "@/types"
import {
  formatCredits,
  formatUsageTime,
  summarizeUsageMeta,
} from "@/lib/usageMeta"
import { getUserFacingApiErrorToastProps } from "@/lib/apiErrors"
import { getLabelChipStyle } from "./labelColors"

const staticCols = [
  { key: "input", label: "table.input", width: 180 },
  { key: "output", label: "table.output", width: 180 },
  { key: "expected_output", label: "table.expectedOutput", width: 180 },
]

// Helper function to truncate text
const truncateText = (text: string, maxLength: number = 60) => {
  if (!text || text.length <= maxLength) return text
  return text.substring(0, maxLength) + "..."
}

export interface EvaluationCaseTableProps {
  cases: MergedEvaluationCase[]
  selectedEvaluator: Evaluator | null
  evaluators: Evaluator[]
  selectedCaseId?: string | null
  selectedIds: string[]
  allSelected: boolean
  isIndeterminate: boolean
  results: EvaluationResult[]
  onSelectRow: (id: string, checked: boolean) => void
  onSelectAll: (checked: boolean) => void
  onOpenCase: (caseData: EvaluationCase, evaluator?: Evaluator | null) => void
}

const EvaluationCaseTable = ({
  cases,
  selectedEvaluator,
  evaluators,
  selectedCaseId,
  selectedIds,
  allSelected,
  isIndeterminate,
  results,
  onSelectRow,
  onSelectAll,
  onOpenCase,
}: EvaluationCaseTableProps) => {
  const { alertToast } = useToast()

  // Stop evaluation API
  const { fetchFn: stopEvaluation, status: stopStatus } = useStopEvaluationApi()

  const { t, ready } = useTranslation()
  if (!ready) return null

  const handleRowClick = (caseData: EvaluationCase) => {
    onOpenCase(caseData)
  }

  const evaluatorNameById = new Map(
    evaluators.map((evaluator) => [evaluator.id, evaluator.name])
  )

  // Handle stop evaluation
  const handleStopEvaluation = async (caseId: string, evaluatorId?: string) => {
    if (!evaluatorId) {
      alertToast({
        title: t("evaluation.evaluationTable.message.error"),
        description: t(
          "evaluation.evaluationTable.message.noEvaluatorSelected"
        ),
      })
      return
    }

    try {
      const requestPayload = makeStopEvaluationRequest(caseId, evaluatorId)
      await stopEvaluation({
        jsonBody: requestPayload,
      })

      alertToast({
        title: t("evaluation.evaluationTable.message.stopped"),
        description: t("evaluation.evaluationTable.message.evaluationStopped"),
      })
    } catch (error) {
      console.error("Failed to stop evaluation:", error)
      alertToast({
        ...getUserFacingApiErrorToastProps(error, t, {
          title: t("evaluation.evaluationTable.message.error"),
          fallbackDescription: "",
        }),
      })
    }
  }

  return (
    <>
      <div
        className="relative w-full min-w-0 max-w-full overflow-x-auto"
        data-copilot-field="table.summary"
        data-copilot-value={summarizeEvaluationTable({
          cases,
          selectedIds,
          selectedEvaluator,
          results,
        })}
      >
        <div className="min-w-[1210px]">
          <table className="w-full bg-white dark:bg-slate-950 rounded-lg shadow-xs border border-gray-200 dark:border-slate-800">
            <thead className="bg-gray-100 dark:bg-slate-900">
              <tr>
                <th
                  className="p-4 text-center align-middle border-b bg-gray-100 dark:bg-slate-900 dark:border-slate-800 z-10"
                  style={{ width: 40, minWidth: 40, maxWidth: 40 }}
                >
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = isIndeterminate
                    }}
                    onChange={(e) => onSelectAll(e.target.checked)}
                    aria-label={t("evaluation.evaluationTable.table.selectAll")}
                  />
                </th>
                {staticCols.map((col, _idx) => (
                  <th
                    key={col.key}
                    className={`p-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-300 border-b bg-gray-100 dark:bg-slate-900 dark:border-slate-800 z-10`}
                    style={{
                      minWidth: col.width,
                      maxWidth: col.width,
                      width: col.width,
                      background: undefined,
                    }}
                  >
                    {t(`evaluation.evaluationTable.${col.label}`)}
                  </th>
                ))}
                {/* Individual column headers for Score, Rubric, Reason */}
                <th
                  className="p-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-300 border-b bg-gray-100 dark:bg-slate-900 dark:border-slate-800"
                  style={{ minWidth: 140, maxWidth: 140, width: 140 }}
                >
                  {t("evaluation.evaluationTable.table.rubric")}
                </th>
                <th
                  className="p-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-300 border-b bg-gray-100 dark:bg-slate-900 dark:border-slate-800"
                  style={{ minWidth: 80, maxWidth: 80, width: 80 }}
                >
                  {t("evaluation.evaluationTable.table.score")}
                </th>
                <th
                  className="p-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-300 border-b bg-gray-100 dark:bg-slate-900 dark:border-slate-800"
                  style={{ minWidth: 140, maxWidth: 160, width: 150 }}
                >
                  {t("evaluation.evaluationTable.table.usage")}
                </th>
                <th
                  className="p-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-300 border-b bg-gray-100 dark:bg-slate-900 dark:border-slate-800"
                  style={{ minWidth: 140, maxWidth: 140, width: 140 }}
                >
                  {t("evaluation.evaluationTable.table.reason")}
                </th>
                <th
                  className="p-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-300 border-b bg-gray-100 dark:bg-slate-900 dark:border-slate-800"
                  style={{ minWidth: 170, maxWidth: 190, width: 180 }}
                >
                  {t("evaluation.evaluationTable.table.evaluators")}
                </th>
                <th
                  className="p-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-300 border-b bg-gray-100 dark:bg-slate-900 dark:border-slate-800"
                  style={{ minWidth: 130, maxWidth: 150, width: 140 }}
                >
                  {t("evaluation.evaluationTable.table.labels")}
                </th>
                <th
                  className="p-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-300 border-b bg-gray-100 dark:bg-slate-900 dark:border-slate-800"
                  style={{ minWidth: 180, maxWidth: 180, width: 180 }}
                >
                  {t("evaluation.evaluationTable.table.note")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-slate-800">
              {cases.map((c) => {
                const isDetailSelected = selectedCaseId === c.id
                const rowCellClassName = isDetailSelected
                  ? "bg-blue-50/80 dark:bg-blue-950/30"
                  : "bg-white group-hover:bg-blue-50 dark:bg-slate-950 dark:group-hover:bg-blue-900"
                const selectedIndicatorClassName = isDetailSelected
                  ? "border-l-2 border-blue-500 dark:border-blue-400"
                  : "border-l-2 border-transparent"
                const assignedEvaluators = c.assignedEvaluators || []
                const caseLabels = c.labels || []
                const isFilterNotAssigned =
                  !!selectedEvaluator && c.selectedEvaluatorAssigned === false
                // Find the evaluation result for this case and the selected evaluator
                // Only search for results if selectedEvaluator has a valid id
                const evalResult =
                  selectedEvaluator?.id && !isFilterNotAssigned
                    ? results.find(
                        (r) =>
                          r.evaluationCaseId === c.id &&
                          r.evaluatorId === selectedEvaluator.id
                      )
                    : undefined
                const responseTimeMs =
                  typeof evalResult?.responseTimeMs === "number"
                    ? Math.max(0, evalResult.responseTimeMs)
                    : undefined
                const formattedResponseTime =
                  formatUsageTime(responseTimeMs) ?? undefined
                const costCredits = evalResult?.costCredits
                const hasUsage =
                  typeof costCredits === "number" ||
                  formattedResponseTime !== undefined
                const isEvaluationRunning =
                  evalResult?.status === "running" ||
                  evalResult?.status === "pending"
                const hasOutput = !!evalResult?.output
                return (
                  <tr
                    key={c.id}
                    className="group transition-colors"
                    data-copilot-field={`table.row.${c.id}.selected`}
                    data-copilot-value={
                      selectedIds.includes(c.id) ? "true" : "false"
                    }
                  >
                    <td
                      className={`p-4 align-top text-center z-10 transition-colors ${rowCellClassName} ${selectedIndicatorClassName}`}
                      style={{ width: 40, minWidth: 40, maxWidth: 40 }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(c.id)}
                        onChange={(e) => onSelectRow(c.id, e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={t(
                          "evaluation.evaluationTable.table.selectRow"
                        )}
                      />
                      <div
                        data-copilot-field={`table.row.${c.id}.status`}
                        data-copilot-value={evalResult?.status || ""}
                        className="hidden"
                      />
                    </td>
                    {/* Input column */}
                    <td
                      key="input"
                      className={`p-4 align-top z-10 transition-colors ${rowCellClassName}`}
                      style={{
                        minWidth: staticCols[0].width,
                        maxWidth: staticCols[0].width,
                        width: staticCols[0].width,
                        background: undefined,
                      }}
                      onClick={() => handleRowClick(c)}
                      data-copilot-field={`table.row.${c.id}.input`}
                      data-copilot-value={safe(c.input || "")}
                    >
                      <div className="flex flex-col min-w-0 h-full justify-between">
                        <p className="text-sm text-gray-900 dark:text-gray-100 leading-relaxed break-words">
                          {truncateText(c.input || "", 60)}
                        </p>
                        {c.attachments && c.attachments.length > 0 && (
                          <div
                            className="flex items-center gap-2 mt-2"
                            data-copilot-field={`table.row.${c.id}.attachments`}
                            data-copilot-value={summarizeAttachments(
                              c.attachments
                            )}
                          >
                            <AttachmentBadge attachments={c.attachments} />
                          </div>
                        )}
                      </div>
                    </td>
                    {/* Output column: use evalResult.output if present */}
                    <td
                      key="output"
                      className={`p-4 align-top z-10 transition-colors ${rowCellClassName}`}
                      style={{
                        minWidth: staticCols[1].width,
                        maxWidth: staticCols[1].width,
                        width: staticCols[1].width,
                        background: undefined,
                      }}
                      onClick={() => handleRowClick(c)}
                      data-copilot-field={`table.row.${c.id}.output`}
                      data-copilot-value={safe(evalResult?.output ?? "")}
                    >
                      <div className="flex-1 min-w-0">
                        {isFilterNotAssigned ? (
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            {t("evaluation.evaluationTable.table.notAssigned")}
                          </span>
                        ) : evalResult &&
                          evalResult.status === "failed" &&
                          !hasOutput ? (
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                              <span className="text-sm font-medium text-red-600 dark:text-red-400">
                                {t("evaluation.evaluationTable.status.failed")}
                              </span>
                            </div>
                            <p className="text-xs text-red-600 dark:text-red-400 leading-relaxed break-words">
                              {t("evaluation.evaluationTable.status.noOutput")}
                            </p>
                          </div>
                        ) : evalResult && !hasOutput && isEvaluationRunning ? (
                          <GeneratingSpinner />
                        ) : (
                          <div className="space-y-2">
                            <p className="text-sm text-gray-900 dark:text-gray-100 leading-relaxed">
                              {truncateText(evalResult?.output ?? "", 60)}
                            </p>
                          </div>
                        )}
                        {evalResult?.output && evalResult?.outputUpdatedAt && (
                          <div
                            className="inline-flex items-center text-xs text-gray-500 dark:text-gray-400 px-2 py-1 bg-gray-100 dark:bg-slate-700 rounded-full"
                            data-copilot-field={`table.row.${c.id}.output.updatedAt`}
                            data-copilot-value={format(
                              new Date(evalResult.outputUpdatedAt),
                              "MMM d, yyyy  HH:mm:ss"
                            )}
                          >
                            {format(
                              new Date(evalResult.outputUpdatedAt),
                              "MMM d, yyyy  HH:mm:ss"
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    {/* Expected Output column */}
                    <td
                      key="expected_output"
                      className={`p-4 align-top z-10 transition-colors ${rowCellClassName}`}
                      style={{
                        minWidth: staticCols[2].width,
                        maxWidth: staticCols[2].width,
                        width: staticCols[2].width,
                        background: undefined,
                      }}
                      onClick={() => handleRowClick(c)}
                      data-copilot-field={`table.row.${c.id}.expectedOutput`}
                      data-copilot-value={safe(c.expectedOutput || "")}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 dark:text-gray-100 leading-relaxed">
                          {truncateText(c.expectedOutput || "", 60)}
                        </p>
                      </div>
                    </td>
                    {/* Rubric column */}
                    <td
                      key="rubric"
                      className={`p-4 align-top text-gray-700 dark:text-gray-300 transition-colors ${rowCellClassName}`}
                      style={{ minWidth: 140, maxWidth: 140, width: 140 }}
                      onClick={() => handleRowClick(c)}
                      data-copilot-field={`table.row.${c.id}.rubric`}
                      data-copilot-value={safe(c.rubric ?? "")}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 dark:text-gray-100 leading-relaxed">
                          {isFilterNotAssigned
                            ? t("evaluation.evaluationTable.table.notAssigned")
                            : truncateText(c.rubric ?? "", 60)}
                        </p>
                      </div>
                    </td>
                    {/* Score column */}
                    <td
                      key="score"
                      className={`p-4 align-top text-gray-700 dark:text-gray-300 transition-colors ${rowCellClassName}`}
                      style={{ minWidth: 80, maxWidth: 80, width: 80 }}
                      onClick={() => handleRowClick(c)}
                      data-copilot-field={`table.row.${c.id}.score`}
                      data-copilot-value={
                        typeof evalResult?.score === "number"
                          ? (evalResult.score as number).toFixed(2)
                          : ""
                      }
                    >
                      {isFilterNotAssigned ? (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {t("evaluation.evaluationTable.table.notAssigned")}
                        </span>
                      ) : evalResult &&
                        typeof evalResult.score !== "number" &&
                        isEvaluationRunning ? (
                        <GeneratingSpinner />
                      ) : (
                        <span>
                          {typeof evalResult?.score === "number"
                            ? evalResult.score.toFixed(2)
                            : t("evaluation.evaluationTable.table.noScore")}
                        </span>
                      )}
                    </td>
                    {/* Usage column */}
                    <td
                      key="usage"
                      className={`p-4 align-top text-gray-700 dark:text-gray-300 transition-colors ${rowCellClassName}`}
                      style={{ minWidth: 140, maxWidth: 160, width: 150 }}
                      onClick={() => handleRowClick(c)}
                      data-copilot-field={`table.row.${c.id}.usage`}
                      data-copilot-value={summarizeUsageMeta({
                        responseTimeMs,
                        costCredits,
                      })}
                    >
                      {isFilterNotAssigned ? (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {t("evaluation.evaluationTable.table.notAssigned")}
                        </span>
                      ) : hasUsage ? (
                        <div className="space-y-1">
                          {typeof costCredits === "number" && (
                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                              {formatCredits(costCredits)} Credits
                            </div>
                          )}
                          {formattedResponseTime && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {formattedResponseTime} s
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {t("evaluation.evaluationTable.table.noUsage")}
                        </span>
                      )}
                    </td>
                    {/* Reason column */}
                    <td
                      key="reason"
                      className={`p-4 align-top text-gray-700 dark:text-gray-300 transition-colors ${rowCellClassName}`}
                      style={{ minWidth: 140, maxWidth: 140, width: 140 }}
                      onClick={() => handleRowClick(c)}
                      data-copilot-field={`table.row.${c.id}.reason`}
                      data-copilot-value={safe(evalResult?.reason ?? "")}
                    >
                      {isFilterNotAssigned ? (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {t("evaluation.evaluationTable.table.notAssigned")}
                        </span>
                      ) : evalResult && evalResult.status === "failed" ? (
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                            <span className="text-sm font-medium text-red-600 dark:text-red-400">
                              {t("evaluation.evaluationTable.status.failed")}
                            </span>
                          </div>
                          <p className="text-xs text-red-600 dark:text-red-400 leading-relaxed break-words">
                            {truncateText(evalResult.reason ?? "", 100)}
                          </p>
                        </div>
                      ) : evalResult &&
                        (!evalResult.reason || evalResult.reason === "") &&
                        isEvaluationRunning ? (
                        <div className="flex items-center justify-between">
                          <GeneratingSpinner />
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleStopEvaluation(c.id, selectedEvaluator?.id)
                            }}
                            className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20 rounded transition-colors"
                            title={t(
                              "evaluation.evaluationTable.actions.stopEvaluation"
                            )}
                            disabled={stopStatus === "loading"}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 dark:text-gray-100 leading-relaxed break-words">
                            {truncateText(evalResult?.reason ?? "", 60)}
                          </p>
                        </div>
                      )}
                      {evalResult?.reason && evalResult?.updatedAt && (
                        <div
                          className="inline-flex items-center text-xs text-gray-500 dark:text-gray-400 px-2 py-1 bg-gray-100 dark:bg-slate-700 rounded-full"
                          data-copilot-field={`table.row.${c.id}.reason.updatedAt`}
                          data-copilot-value={format(
                            new Date(evalResult.updatedAt),
                            "MMM d, yyyy  HH:mm:ss"
                          )}
                        >
                          {format(
                            new Date(evalResult.updatedAt),
                            "MMM d, yyyy  HH:mm:ss"
                          )}
                        </div>
                      )}
                    </td>
                    {/* Evaluators column */}
                    <td
                      key="evaluators"
                      className={`p-4 align-top text-gray-700 dark:text-gray-300 transition-colors ${rowCellClassName}`}
                      style={{ minWidth: 170, maxWidth: 190, width: 180 }}
                      onClick={() => handleRowClick(c)}
                      data-copilot-field={`table.row.${c.id}.evaluators`}
                      data-copilot-value={assignedEvaluators
                        .map(
                          (assignment) =>
                            evaluatorNameById.get(assignment.evaluatorId) ||
                            assignment.evaluatorId
                        )
                        .join(" | ")}
                    >
                      {assignedEvaluators.length === 0 ? (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {t("evaluation.evaluationTable.table.noTesters")}
                        </span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {assignedEvaluators.slice(0, 2).map((assignment) => (
                            <span
                              key={assignment.evaluatorId}
                              className="inline-flex max-w-full items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                            >
                              <span className="truncate">
                                {evaluatorNameById.get(
                                  assignment.evaluatorId
                                ) || assignment.evaluatorId}
                              </span>
                            </span>
                          ))}
                          {assignedEvaluators.length > 2 && (
                            <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400">
                              +{assignedEvaluators.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    {/* Labels column */}
                    <td
                      key="labels"
                      className={`p-4 align-top text-gray-700 dark:text-gray-300 transition-colors ${rowCellClassName}`}
                      style={{ minWidth: 130, maxWidth: 150, width: 140 }}
                      onClick={() => handleRowClick(c)}
                      data-copilot-field={`table.row.${c.id}.labels`}
                      data-copilot-value={caseLabels
                        .map((label) => label.name)
                        .join(" | ")}
                    >
                      {caseLabels.length === 0 ? (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {t("evaluation.evaluationTable.table.noLabels")}
                        </span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {caseLabels.slice(0, 2).map((label) => (
                            <span
                              key={label.id}
                              className="inline-flex max-w-full items-center rounded-full border px-2 py-0.5 text-xs"
                              style={getLabelChipStyle(label.color)}
                            >
                              <span className="truncate">{label.name}</span>
                            </span>
                          ))}
                          {caseLabels.length > 2 && (
                            <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400">
                              +{caseLabels.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    {/* Note column */}
                    <td
                      key="note"
                      className={`p-4 align-top text-gray-700 dark:text-gray-300 transition-colors ${rowCellClassName}`}
                      style={{ minWidth: 180, maxWidth: 180, width: 180 }}
                      onClick={() => handleRowClick(c)}
                      data-copilot-field={`table.row.${c.id}.note`}
                      data-copilot-value={safe(c.note ?? "")}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 dark:text-gray-100 leading-relaxed break-words">
                          {truncateText(c.note ?? "", 60)}
                        </p>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <AverageScoreCard
        cases={cases}
        selectedEvaluator={selectedEvaluator ?? undefined}
      />
    </>
  )
}

export default EvaluationCaseTable
